<template>
    <div id="fileviewer">
        <div id="content" class="flex-fill" ref="contentElement">
            <p v-if="!artefact.content && !isEditing">
                <i>No content.</i>
            </p>
            <Form
                v-if="artefact.mimetype === 'application/schema+json'"
                v-model="artefact"
                class="flex-fill"
                @updating="(e: boolean) => (isUpdating = e)"
                :isEditing="isEditing"
            />
            <DMN
                v-else-if="artefact.mimetype === 'application/dmn+xml'"
                v-model="artefact"
                class="flex-fill"
                @updating="(e: boolean) => (isUpdating = e)"
                :isEditing="isEditing"
            />
            <img
                v-else-if="contentFlavor === ContentFlavor.IMAGE"
                :src="blobUrl"
            />
            <div
                v-else-if="contentFlavor === ContentFlavor.GOOGLEDRIVE"
                class="gdrive flex-fill"
                ref="gdriveElement"
            >
                <iframe border="0" class="embed flex-fill" :src="embedUrl" />
            </div>
            <div
                v-else-if="contentFlavor === ContentFlavor.PDF"
                class="pdf flex-fill"
            >
                <object :data="blobUrl" :type="artefact.mimetype">
                    <div class="button">
                        <Button
                            class="theme-change p-button p-component"
                            icon="pi pi-download"
                            label="Download PDF"
                            @click="download"
                        />
                        <p>
                            <small>
                                Your browser doesn't support PDF preview. Try
                                another browser or download the PDF to view it
                                on your computer.
                            </small>
                        </p>
                    </div>
                </object>
            </div>
            <HtmlViewer
                v-else-if="
                    !plainText &&
                    !isEditing &&
                    artefact.mimetype === 'text/html'
                "
                :content="content"
                class="flex-fill"
            />
            <RichTextEditor
                v-else-if="!plainText && artefact.mimetype.endsWith('markdown')"
                class="text-editor flex-fill"
                v-model="artefact"
                :readingMode="!isEditing"
            />
            <PlainTextEditor
                v-else-if="
                    isEditing &&
                    (plainText || contentFlavor === ContentFlavor.TEXTUAL)
                "
                class="text-editor flex-fill"
                v-model="artefact"
            />
            <hljsPlugin.component
                v-else-if="contentFlavor === ContentFlavor.TEXTUAL"
                :language="language"
                :code="content"
                :autodetect="!language"
                class="text-editor flex-fill"
            />
            <div v-else>
                Cannot show
                <span style="font-weight: bold">{{ artefact.mimetype }}</span>
                file type.
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import hljsPlugin from "@highlightjs/vue-plugin";

import hljs from "highlight.js";
import darkStyles from "highlight.js/styles/github-dark-dimmed.min.css?raw";
import lightStyles from "highlight.js/styles/github.min.css?raw";
import { computed, onUpdated, ref, toRef, watch } from "vue";
import { useRoute } from "vue-router";

import DMN from "@/components/DMN.vue";
import Form from "@/components/Form.vue";
import HtmlViewer from "@/components/HtmlViewer.vue";
import PlainTextEditor from "@/components/PlainTextEditor.vue";
import RichTextEditor from "@/components/RichTextEditor.vue";
import { useEditManager } from "@/composables/edit-manager";
import type { Artefact } from "@/lib/artefacts";
import { ContentFlavor, getMimetypeFlavor } from "@/lib/contentFlavor";
import { useUIManager } from "@/stores/ui-store";
import { downloadBlob, ensureFilenameExtension, mime } from "@/utils";

hljs.registerAliases("bpmn", { languageName: "xml" });

const props = withDefaults(
    defineProps<{
        showToastOnSave?: boolean;
        plainText?: boolean;
        artefact: Artefact;
    }>(),
    {
        showToastOnSave: true,
        plainText: false,
    }
);

const ui = useUIManager();

const plainText = toRef(() => props.plainText);
const artefact = toRef(() => props.artefact);
const route = useRoute();
const { isEditing } = useEditManager();

const isUpdating = ref(false);

const content = ref("");
const blobUrl = ref("");

const contentElement = ref<HTMLElement>();
const gdriveElement = ref<HTMLElement>();

const contentFlavor = computed(() => {
    return getMimetypeFlavor(artefact.value?.mimetype);
});

const language = computed(() => mime.getExtension(artefact.value.mimetype));

const embedUrl = computed((): string => {
    if (contentFlavor.value === ContentFlavor.GOOGLEDRIVE) {
        const obj = JSON.parse(content.value);
        return isEditing.value ? obj.url : obj.embedUrl;
    } else if (artefact.value?.mimetype === "application/pdf") {
        return blobUrl.value;
    }
    return "";
});

/**
 * Dowload the file using a dataurl link
 */
const download = async () => {
    downloadBlob(
        blobUrl.value,
        ensureFilenameExtension(artefact.value.name, artefact.value.mimetype)
    );
};

// Get file contents as string or dataURL (in case of being an image)
const setFileContents = async (artefact_: Artefact) => {
    content.value = artefact_.content;
    blobUrl.value = await artefact_.getDataUrl();
};

onUpdated(() => {
    if (
        artefact.value?.mimetype.endsWith("markdown") &&
        !isEditing.value &&
        contentElement.value
    ) {
        contentElement.value
            .querySelectorAll<HTMLElement>("pre code:not(.hljs)")
            .forEach(hljs.highlightElement);
        if (route.hash) {
            const anchor = document.querySelector(`a[href='${route.hash}']`);
            if (anchor) {
                anchor.scrollIntoView();
            }
        }
    }
});

watch(
    artefact,
    async (newArtefact) => {
        if (newArtefact) {
            await setFileContents(newArtefact);
        } else {
            content.value = "";
            blobUrl.value = "";
        }
    },
    { immediate: true }
);

/**
 * Apply a raw stylesheet. This allows us to toggle between light and dark styles.
 */
const applyStylesheet = (styleContent: typeof import("*.css")) => {
    // Remove any existing dynamic stylesheet
    const existingStyle = document.getElementById("dynamic-highlight-theme");
    if (existingStyle) {
        existingStyle.remove();
    }

    // Create and append the new stylesheet
    const styleElement = document.createElement("style");
    styleElement.id = "dynamic-highlight-theme";
    styleElement.textContent = styleContent as string;
    document.head.appendChild(styleElement);
};

// Toggle highlightjs styling based on dark mode
watch(
    ui.darkMode,
    (newDarkMode) => {
        applyStylesheet(newDarkMode ? darkStyles : lightStyles);
    },
    { immediate: true }
);
</script>

<style scoped lang="scss">
.flex-fill {
    display: flex;
    flex-direction: row;

    > * {
        flex: 1;
    }
}

.actions {
    flex: 0;
}

#fileviewer {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    max-width: 100%;

    .filepath {
        align-items: center;
        gap: 0.2rem;

        .title {
            margin-right: 1rem;
            margin-bottom: 0;

            &[contenteditable="true"] {
                outline: 1px ridge;
                outline-color: var(--stekz-primary-color);
            }
        }

        .edit {
            box-shadow: none !important;
            opacity: 0;
            transform: translateX(-2rem);
            transition: ease-in-out 150ms all;
        }

        &:hover,
        &:focus,
        &:active {
            .edit {
                opacity: 1;
                transform: translateX(0);
            }
        }
    }

    :deep(.p-toggleable-content) {
        display: flex;
        flex-direction: column;
        flex: 1;

        .p-panel-content {
            min-height: 100px;
            display: flex;
            flex-direction: column;
            flex: 1;
        }
    }

    #content {
        flex: 1;
        padding: 0.25rem;
        overflow-x: auto;
        height: 100%;

        img {
            align-self: start;
            object-fit: contain;
        }

        .text-editor {
            padding: 0.5em 1em;

            // Make the editor scroll by itself
            :deep(c-text-editor::part(editor-document)) {
                overflow-y: auto;
            }

            :deep(c-text-editor::part(main)) {
                position: absolute;
                inset: 0;
            }
        }

        :deep(.markdown) {
            display: flex;
            flex: 1;
            flex-direction: column;

            c-text-editor {
                display: flex;
                flex: 1;
                flex-direction: column;
            }
        }

        .notfound {
            font-style: italic;
            color: darkgrey;
            text-align: center;
        }

        .pdf {
            object,
            iframe {
                border: 0;
                width: 100%;
                height: 90vh;
                text-align: center;
            }

            .button {
                max-width: 400px;
                margin: auto;
            }
        }

        .gdrive {
            text-align: center;

            .embed {
                border: 0;
                width: 100%;
                height: 90vh;
            }

            a {
                text-decoration: none;
            }
        }

        // Disable top margin for code files
        :deep(> div > pre) {
            margin: 0;
        }
    }

    &.has-gdrive {
        border-top: 1px solid #dee2e6;

        :deep(.p-panel-content, #content) {
            padding: 0;
        }
    }
}
</style>

<style lang="css">
@import url("bpmn-js/dist/assets/bpmn-font/css/bpmn.css");
</style>
